import {FormParams} from "../Constants/PromoInterfaces";
import {useTranslation} from "react-i18next";
import {Grid, InputAdornment, TextField, Tooltip} from "@mui/material";
import {Controller} from "react-hook-form";
import {
    ALPHANUMERIC_REG_EXP,
    CURRENCY_REG_EXP,
    PROMO_TYPOLOGY_CREDIT_CARD,
    PROMO_TYPOLOGY_SHORT_TERM
} from "../../../component/Constants";
import * as React from "react";
import {ClockPickerView, DatePicker, DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {checkValueWithInterval} from "../../../component/Utils";
import {InfoRounded} from "@mui/icons-material";
import { useCallback } from "react";

const SubscriptionLastRows = ({watch, setValue, control, updateInitialTypology, isUpdate}: FormParams) => {
    const shouldDisableTime = useCallback((time: number, clockTime: ClockPickerView) => {
        return true;
    }, []);
    const {t} = useTranslation(['salesmarketing', 'translation']);
    return (
        <>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="monthlyFeeDiscountPercentage"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth
                            label={t('promo.monthlyFeeDiscountPercentage')}
                            value={field.value}
                            disabled={watch('monthlyFeeDiscountAmount')!==''}
                            placeholder={watch("monthlyFeeDiscountAmount") ? "--" : ""}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">%</InputAdornment>,
                            }}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="monthlyFeeDiscountAmount"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth
                            label={t('promo.monthlyFeeDiscountAmount')}
                            disabled={watch('monthlyFeeDiscountPercentage')!==''}
                            InputLabelProps={{shrink: watch("monthlyFeeDiscountPercentage") ? watch("monthlyFeeDiscountPercentage") : undefined }}
                            placeholder={watch("monthlyFeeDiscountPercentage") ? "--" : ""}
                            onChange={(e) => {
                                if (e.target.value === "" || CURRENCY_REG_EXP.test(e.target.value)  ||e.target.value.startsWith(',') ||e.target.value.startsWith('.')) {
                                    setValue!("monthlyFeeDiscountAmount", checkValueWithInterval(e.target.value, 0, 1000000));
                                    setValue!("monthlyFeeDiscountPercentage", '');
                                }
                            }}
                            value={field.value}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="numberOfMonths"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth
                            label={t('promo.numberOfMonths')}
                            type={"number"}
                            value={field.value}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="description"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth
                            label={t('promo.description')}
                            value={field.value}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="monthlyStartDate"
                    render={({field, fieldState}) => (
                        <DateTimePicker
                            label={t('promo.monthlyStartDate')}
                            value={field.value}
                            onChange={field.onChange}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    required
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />}
                            shouldDisableTime={shouldDisableTime}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="monthlyEndDate"
                    render={({field, fieldState}) => (
                        <DateTimePicker
                            label={t('promo.monthlyEndDate')}
                            value={field.value}
                            onChange={field.onChange}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    required
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />}
                            shouldDisableTime={shouldDisableTime}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="promoCode"
                    render={({field, fieldState}) => (
                        <TextField
                            inputProps={{maxLength: 15}}
                            required
                            disabled={isUpdate && field.value && updateInitialTypology!== PROMO_TYPOLOGY_CREDIT_CARD}
                            fullWidth
                            InputProps={{
                                endAdornment: <Tooltip arrow title={t('promo.createForm.promoCodeLength')}><InfoRounded
                                    color="primary"/></Tooltip>
                            }}
                            label={t('promo.promoCode')}
                            value={field.value}
                            onChange={(e) => {
                                if (e.target.value === "" || ALPHANUMERIC_REG_EXP.test(e.target.value)) {
                                    setValue!("promoCode", e.target.value)
                                }
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="yearlyFeeDiscountPercentage"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth
                            label={t('promo.yearlyFeeDiscountPercentage')}
                            value={field.value}
                            disabled={watch('yearlyFeeDiscountAmount')!==''}
                            placeholder={watch("yearlyFeeDiscountAmount") ? "--" : ""}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">%</InputAdornment>,
                            }}
                        />
                    )}/>
            </Grid>
            <Grid item xs={3}>
                <Controller
                    control={control}
                    name="yearlyFeeDiscountAmount"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth
                            label={t('promo.yearlyFeeDiscountAmount')}
                            value={field.value}
                            disabled={watch('yearlyFeeDiscountPercentage')!==''}
                            InputLabelProps={{shrink: watch("yearlyFeeDiscountPercentage") ? watch("yearlyFeeDiscountPercentage") : undefined }}
                            placeholder={watch("yearlyFeeDiscountPercentage") ? "--" : ""}
                            onChange={(e) => {
                                if (e.target.value === "" || CURRENCY_REG_EXP.test(e.target.value)  ||e.target.value.startsWith(',') ||e.target.value.startsWith('.')) {
                                    setValue!("yearlyFeeDiscountAmount", checkValueWithInterval(e.target.value, 0, 1000000));
                                    setValue!("yearlyFeeDiscountPercentage", '');
                                }
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}/>
            </Grid>
        </>
    )
}
export default SubscriptionLastRows